/**
 * CompanyContextBanner Component
 *
 * Displays company context information during auth flows.
 *
 * @module @puffin-core/auth-shell/CompanyContextBanner
 */

import { useState } from 'react';
import PropTypes from 'prop-types';

import { useAuthShellTranslate } from '../hooks/useAuthShellTranslate.js';

// Each mode's catalog key (PACKAGE-I18N-SEAT: the words were English written here).
const MODE_LABEL_KEYS = {
  login: 'authShell.companyBanner.login',
  register: 'authShell.companyBanner.register',
  device: 'authShell.companyBanner.device',
};

/**
 * CompanyContextBanner Component
 *
 * @param {Object} props
 * @param {string} props.name - Company name (required)
 * @param {string} [props.logo] - Company logo URL
 * @param {string} [props.subdomain] - Company subdomain
 * @param {string} [props.domainSuffix] - The host the subdomain lives under (e.g. 'plannerpuffin.com'). Without it there
 *   is no address line: the full host or nothing, never a bare subdomain.
 * @param {string} [props.mode='login'] - Auth mode ('login', 'register', 'device')
 */
function CompanyContextBanner({ name, logo, subdomain, domainSuffix, mode = 'login' }) {
  const t = useAuthShellTranslate();
  // The URL whose image failed to load. Keyed on the URL, so a NEW logo is tried again; the failure is state, never a
  // style written onto the DOM node behind React's back.
  const [failedLogo, setFailedLogo] = useState(null);
  const showLogo = Boolean(logo) && failedLogo !== logo;

  // Static context, so no `aria-live`: nothing here changes while the page is read.
  return (
    <div className="auth-company-banner" data-testid="company-context-banner">
      <div className="auth-company-banner-content">
        {showLogo && (
          // Decorative (WCAG technique H2): the company name beside it is the text.
          <img
            src={logo}
            alt=""
            aria-hidden="true"
            className="auth-company-banner-logo"
            // TENANT-LOGO: the logo lives on the store's own site; a sign-in page's address names the store's
            // subdomain and the person's path, and that site has no reason to learn either.
            referrerPolicy="no-referrer"
            onError={() => setFailedLogo(logo)}
          />
        )}
        <div className="auth-company-banner-info">
          <span className="auth-company-banner-label">
            {t(MODE_LABEL_KEYS[mode] || MODE_LABEL_KEYS.login)}
          </span>
          <span className="auth-company-banner-name">{name}</span>
          {subdomain && domainSuffix && (
            <span className="auth-company-banner-subdomain">
              {subdomain}.{domainSuffix}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

CompanyContextBanner.propTypes = {
  name: PropTypes.string.isRequired,
  logo: PropTypes.string,
  subdomain: PropTypes.string,
  domainSuffix: PropTypes.string,
  mode: PropTypes.oneOf(['login', 'register', 'device']),
};

// React 19 ignores `defaultProps` on function components, so the block that
// stood here was inert. `mode` already defaults in the parameter list;
// `logo` and `subdomain` were explicit `undefined`.

export default CompanyContextBanner;
